import { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { API_BASE_URL } from '../../../../lib/constants';
import { useNotification } from '../../../../contexts/NotificationContext';

/**
 * Unified OT Booking service hook — Add or Edit based on uhid_no presence.
 */
export const useOTBooking = (uhid_no) => {
  const { showNotification } = useNotification();
  const isEdit = !!uhid_no;
  const [loading, setLoading] = useState(true);
  const [existingData, setExistingData] = useState(null);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [nextIpNo, setNextIpNo] = useState('');

  useEffect(() => {
    if (isEdit) {
      axios.get(`${API_BASE_URL}/get_ot_booking_by_uhid/${uhid_no}`, { withCredentials: true })
        .then(res => {
          const d = res.data;
          const boolFields = [
            'preoperative_tests_required',
            'special_equipment_needed',
            'blood_products_required',
            'fasting_instructions_given',
            'preoperative_medications_provided',
            'consent_forms_completed',
            'is_major_ot'
          ];
          const normalized = { ...d };
          boolFields.forEach(f => {
            normalized[f] = Number(d[f]) === 1 || d[f] === true || String(d[f]).toLowerCase() === 'true';
          });

          setExistingData({
            ...normalized,
            allergies_medical_alerts: typeof d.allergies_medical_alerts === 'string' && d.allergies_medical_alerts.length > 0
              ? d.allergies_medical_alerts.split(',').map(s => s.trim()) : [],
            surgery_datetime: d.surgery_datetime ? format(new Date(d.surgery_datetime), "yyyy-MM-dd'T'HH:mm") : '',
            booking_datetime: d.booking_datetime ? format(new Date(d.booking_datetime), "yyyy-MM-dd'T'HH:mm") : '',
            actual_start_time: d.actual_start_time ? format(new Date(d.actual_start_time), "yyyy-MM-dd'T'HH:mm") : '',
            actual_end_time: d.actual_end_time ? format(new Date(d.actual_end_time), "yyyy-MM-dd'T'HH:mm") : '',
          });
        })
        .catch(() => showNotification('error', 'Failed to fetch OT booking details.'))
        .finally(() => setLoading(false));
    } else {
      Promise.all([
        axios.get(`${API_BASE_URL}/get_ot_bookings`, { withCredentials: true }),
        axios.get(`${API_BASE_URL}/get_all_patients`, { withCredentials: true }),
        axios.get(`${API_BASE_URL}/ot_booking_next_ip`, { withCredentials: true }),
      ])
        .then(([bookingsRes, patientsRes, nextIpRes]) => {
          const bookedUHIDs = new Set((bookingsRes.data?.ot_bookings || []).map(b => b.uhid_no));
          setFilteredPatients((patientsRes.data || []).filter(p => !bookedUHIDs.has(p.uhid_no)));
          setNextIpNo(nextIpRes.data.next_ip_no);
        })
        .catch(() => showNotification('error', 'Failed to load booking form data.'))
        .finally(() => setLoading(false));
    }
  }, [uhid_no, isEdit, showNotification]);

  const submit = async (payload) => isEdit
    ? axios.post(`${API_BASE_URL}/update_ot_booking_by_uhid/${uhid_no}`, payload, { withCredentials: true })
    : axios.post(`${API_BASE_URL}/insert_ot_booking`, payload, { withCredentials: true });

  return { isEdit, loading, existingData, filteredPatients, nextIpNo, submit };
};
